import React, { ReactNode } from 'react';
import cn from 'classnames';
import { CircledIIcon, FileIcon, GreenCheckMarkIcon } from '@repo/icons';
import { SettingsInlineButton } from '@views/Settings/components';
import { HighAlertIcon, MedAlertIcon } from '@repo/icons/AlertIcons';
import { Tooltip, Overlay } from '@repo/components/tooltips';
import { localizeNumber } from '@repo/utils/helpers/number';
import { CSV_UPLOAD_TEMPLATE_URL, PIIFields } from '../../vars';
import { CSVModalButton, CSVModalButtonRow } from '../../Buttons';
import styles from './Review.module.scss';

interface GeneralCSVErrors {
  row: boolean;
  column: boolean;
}

interface GeneralCSVWarnings {
  row: boolean;
  column: boolean;
}

const StatusIcon = ({
  hasWarning,
  hasError,
  forceDarkMode,
}: {
  hasWarning?: boolean;
  hasError?: boolean;
  forceDarkMode: boolean;
}) => {
  if (hasError) {
    return <HighAlertIcon />;
  }

  if (hasWarning) {
    return (
      <MedAlertIcon className={cn(styles.warningIcon, forceDarkMode && styles.forceDarkMode)} />
    );
  }

  return <GreenCheckMarkIcon />;
};

const rowWarningMsg = (warnedRowCount: number) => (
  <>
    {warnedRowCount} row{warnedRowCount > 1 ? 's have' : ' has'} some values that are
    <br />
    not formatted correctly
  </>
);

const rowErrorMsg = (hasNoRows: boolean, erroredRowCount: number) => {
  if (hasNoRows) {
    return (
      <>
        No valid rows were found.
        <br />
        There is no contact data to import.
      </>
    );
  }

  return (
    <>
      {erroredRowCount} row{erroredRowCount > 1 ? 's are' : ' is'} missing contact information
      <br />
      and cannot be imported
    </>
  );
};

const columnMsg = (
  noViableHeaders: boolean,
  noImportableHeaders: boolean,
  unsupportedFields: string[],
) => {
  if (noViableHeaders) {
    return (
      <>
        No supported column headers
        <br />
        were detected
      </>
    );
  }

  if (noImportableHeaders) {
    return (
      <>
        No contact identifier column
        <br />
        headers were detected
      </>
    );
  }

  if (unsupportedFields.length > 0) {
    return (
      <>
        Unsupported column headers detected
        <ul>
          {unsupportedFields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </>
    );
  }

  return '';
};

export const AlertBox = ({
  status,
  title,
  children,
  className,
  forceDarkMode,
}: {
  status: 'error' | 'warning';
  title: string;
  children: ReactNode;
  className?: string;
  forceDarkMode?: boolean;
}) => {
  return (
    <div
      className={cn(
        styles.box,
        status === 'error' ? styles.boxError : styles.boxWarning,
        className,
      )}
    >
      {status === 'error' ? (
        <HighAlertIcon />
      ) : (
        <MedAlertIcon className={cn(styles.warningIcon, forceDarkMode && styles.forceDarkMode)} />
      )}
      <div className={cn(styles.title, forceDarkMode && styles.forceDarkMode)}>{title}</div>
      <div className={styles.boxCopy}>{children}</div>
    </div>
  );
};

const alertBoxTitle = (
  errors: GeneralCSVErrors,
  warnings: GeneralCSVWarnings,
  hasNoHeadersFromTemplate: boolean,
  hasImportableHeaders: boolean,
  hasNoRows: boolean,
  hasNoImportableRows: boolean,
) => {
  if (hasNoHeadersFromTemplate) {
    return 'No supported column headers';
  }

  if (!hasImportableHeaders) {
    return 'No identifier columns detected';
  }

  if (hasNoRows) {
    return 'CSV has no rows';
  }

  if (hasNoImportableRows) {
    return 'No importable rows';
  }

  if (errors.row) {
    return 'Some rows cannot be imported';
  }

  if (warnings.row) {
    return 'Some data not properly formatted';
  }

  if (warnings.column) {
    return 'Unsupported column headers';
  }

  return 'Warning';
};

const alertBoxMessage = (
  errors: GeneralCSVErrors,
  warnings: GeneralCSVWarnings,
  hasNoHeadersFromTemplate: boolean,
  hasImportableHeaders: boolean,
  hasNoRows: boolean,
  hasNoImportableRows: boolean,
  importableRowCount: number,
  erroredRowCount: number,
  warnedRowCount: number,
  errorCSV: string,
  errorCSVFileName: string,
) => {
  if (hasNoHeadersFromTemplate) {
    return (
      <p>
        No supported column headers were detected in the uploaded CSV. Please refer to our{' '}
        <a href={CSV_UPLOAD_TEMPLATE_URL} target="_blank">
          formatting guidelines
        </a>
        .
      </p>
    );
  }

  if (!hasImportableHeaders) {
    return (
      <p>
        At least one column must provide a way to identify a person on each row. This can be any one
        of {PIIFields}. Please refer to our{' '}
        <a href={CSV_UPLOAD_TEMPLATE_URL} target="_blank">
          formatting guidelines
        </a>
        .
      </p>
    );
  }

  if (hasNoRows) {
    return (
      <p>No rows have been included in the uploaded CSV. There is no contact data to import.'</p>
    );
  }

  if (hasNoImportableRows) {
    return (
      <p>
        No rows can be imported because none were found with valid contact information. Each row
        must have one of {PIIFields} in order for Clay to create a contact. Please refer to our{' '}
        <a href={CSV_UPLOAD_TEMPLATE_URL} target="_blank">
          formatting guidelines
        </a>
        . You can also{' '}
        <a href={errorCSV} download={errorCSVFileName}>
          download your CSV with error information
        </a>
        .
      </p>
    );
  }

  if (errors.row) {
    return (
      <>
        <p>
          <b>
            {localizeNumber(erroredRowCount)} row{erroredRowCount > 1 ? 's' : ''}
          </b>{' '}
          {erroredRowCount > 1 ? 'are' : 'is'} missing contact information and cannot be imported.
          Please{' '}
          <a href={errorCSV} download={errorCSVFileName}>
            download your CSV with errors appended
          </a>{' '}
          for further information.
        </p>
        <p>
          The remaining{' '}
          <b>
            {localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}
          </b>{' '}
          can still be imported without any further changes, or you can fix the errors shown in the
          CSV above and resubmit.
        </p>
      </>
    );
  }

  if (warnings.row && warnings.column) {
    return (
      <>
        <p>
          <b>
            {localizeNumber(warnedRowCount)} row{warnedRowCount > 1 ? 's' : ''}
          </b>{' '}
          {warnedRowCount > 1 ? 'have' : 'has'} some values that are not formatted correctly. Please{' '}
          <a href={errorCSV} download={errorCSVFileName}>
            download your CSV with formatting warnings appended
          </a>{' '}
          for further information.
        </p>
        <p>
          Also, some of the included columns are not supported by Clay. Data found in these columns
          will be included as a note for each contact created. Please refer to our{' '}
          <a href={CSV_UPLOAD_TEMPLATE_URL} target="_blank">
            formatting guidelines
          </a>{' '}
          to see all supported column headers.
        </p>
        <p>
          All{' '}
          <b>
            {localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}
          </b>{' '}
          can still be imported without any further changes, or you can fix the warnings indicated
          here and resubmit.
        </p>
      </>
    );
  }

  if (warnings.row) {
    return (
      <>
        <p>
          <b>
            {localizeNumber(warnedRowCount)} row{warnedRowCount > 1 ? 's' : ''}
          </b>{' '}
          {warnedRowCount > 1 ? 'have' : 'has'} some values that are not formatted correctly. Please{' '}
          <a href={errorCSV} download={errorCSVFileName}>
            download your CSV with formatting warnings appended
          </a>{' '}
          for further information.
        </p>
        <p>
          All{' '}
          <b>
            {localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}
          </b>{' '}
          can still be imported without any further changes, or you can fix the warnings indicated
          shown in the CSV above and resubmit.
        </p>
      </>
    );
  }

  if (warnings.column) {
    return (
      <>
        <p>
          Some of the included columns are not supported by Clay. Data found in these columns will
          be included as a note for each contact created. Please refer to our{' '}
          <a href={CSV_UPLOAD_TEMPLATE_URL} target="_blank">
            formatting guidelines
          </a>{' '}
          to see all supported column headers.
        </p>
        <p>
          All{' '}
          <b>
            {localizeNumber(importableRowCount)} row{importableRowCount > 1 ? 's' : ''}
          </b>{' '}
          can still be imported without any further changes, or you can remove or fix unsupported
          column headers and resubmit.
        </p>
      </>
    );
  }

  return (
    <>
      Please{' '}
      <a href={errorCSV} download={errorCSVFileName}>
        download your CSV with appended details
      </a>{' '}
      for further information.
    </>
  );
};

export default function ReviewView({
  errors,
  warnings,
  fileName,
  backToUpload,
  hasNoHeadersFromTemplate,
  hasImportableHeaders,
  detectedUnsupportedFields,
  erroredRowCount,
  warnedRowCount,
  importableRowCount,
  hasNoRows,
  hasNoImportableRows,
  downloadableCSV,
  proceedToImportStep,
  forceDarkMode,
}: {
  errors: GeneralCSVErrors;
  warnings: GeneralCSVWarnings;
  fileName: string;
  backToUpload: () => void;
  hasNoHeadersFromTemplate: boolean;
  hasImportableHeaders: boolean;
  detectedUnsupportedFields: string[];
  erroredRowCount: number;
  warnedRowCount: number;
  importableRowCount: number;
  hasNoRows: boolean;
  hasNoImportableRows: boolean;
  downloadableCSV: string;
  proceedToImportStep: () => void;
  forceDarkMode: boolean;
}) {
  const hasError = errors.row || errors.column;
  const hasWarning = warnings.row || warnings.column;

  const hasRowsError = errors.row || hasNoRows;

  return (
    <>
      <div className={styles.selectedFile}>
        <div className={styles.title}>
          <FileIcon />
          <div className={styles.fileName}>{fileName.replace('.csv', '')}</div>
          <div className={styles.fileExtension}>.csv</div>
        </div>
        <SettingsInlineButton className={styles.reuploadBtn} onClick={backToUpload}>
          Re-upload
        </SettingsInlineButton>
      </div>
      <ul className={styles.checklist}>
        <li className={cn(errors.column && styles.error, warnings.column && styles.warning)}>
          <span className={styles.label}>
            Columns are valid
            {(errors.column || warnings.column) && (
              <Tooltip
                placement="top"
                overlay={
                  <Overlay className={styles.tooltip}>
                    {columnMsg(
                      hasNoHeadersFromTemplate,
                      !hasImportableHeaders,
                      detectedUnsupportedFields,
                    )}
                  </Overlay>
                }
              >
                <CircledIIcon width="13" />
              </Tooltip>
            )}
          </span>
          <StatusIcon
            hasError={errors.column}
            hasWarning={warnings.column}
            forceDarkMode={forceDarkMode}
          />
        </li>

        <li className={cn(hasRowsError && styles.error)}>
          <span className={styles.label}>
            Rows have contact info
            {hasRowsError && (
              <Tooltip
                placement="top"
                overlay={
                  <Overlay className={styles.tooltip}>
                    {rowErrorMsg(hasNoRows, erroredRowCount)}
                  </Overlay>
                }
              >
                <CircledIIcon width="13" />
              </Tooltip>
            )}
          </span>
          <StatusIcon hasError={hasRowsError} forceDarkMode={forceDarkMode} />
        </li>

        <li className={cn(warnings.row && styles.warning)}>
          <span className={styles.label}>
            Values properly formatted
            {warnings.row && (
              <Tooltip
                placement="top"
                overlay={
                  <Overlay className={styles.tooltip}>{rowWarningMsg(warnedRowCount)}</Overlay>
                }
              >
                <CircledIIcon width="13" />
              </Tooltip>
            )}
          </span>
          <StatusIcon hasWarning={warnings.row} forceDarkMode={forceDarkMode} />
        </li>
      </ul>

      {(hasError || hasWarning || hasNoRows) && (
        <AlertBox
          status={hasError ? 'error' : 'warning'}
          title={alertBoxTitle(
            errors,
            warnings,
            hasNoHeadersFromTemplate,
            hasImportableHeaders,
            hasNoRows,
            hasNoImportableRows,
          )}
          forceDarkMode={forceDarkMode}
        >
          {alertBoxMessage(
            errors,
            warnings,
            hasNoHeadersFromTemplate,
            hasImportableHeaders,
            hasNoRows,
            hasNoImportableRows,
            importableRowCount,
            erroredRowCount,
            warnedRowCount,
            downloadableCSV,
            `${fileName.replace('.csv', '')}-errors.csv`,
          )}
        </AlertBox>
      )}
      <CSVModalButtonRow>
        <CSVModalButton onClick={backToUpload} secondary forceDarkMode={forceDarkMode}>
          Back
        </CSVModalButton>
        <CSVModalButton
          disabled={errors.column || hasNoRows || hasNoImportableRows}
          onClick={proceedToImportStep}
          forceDarkMode={forceDarkMode}
        >
          Import
        </CSVModalButton>
      </CSVModalButtonRow>
    </>
  );
}
